<template>
    <div class="course-detail-container fullscreen-page">
      <!-- 页面加载状态 -->
      <div v-if="pageLoading" class="page-loading">
        <div v-loading="pageLoading"
             element-loading-text="正在加载课程详情..."
             element-loading-spinner="el-icon-loading"
             element-loading-background="rgba(0, 0, 0, 0.8)"
             style="height: 200px; display: flex; align-items: center; justify-content: center;">
        </div>
      </div>

      <!-- 页面错误状态 -->
      <div v-else-if="pageError" class="page-error">
        <div class="error-content">
          <i class="el-icon-warning-outline"></i>
          <h3>页面加载失败</h3>
          <p>无法加载课程详情，请检查网络连接或刷新页面重试</p>
          <el-button type="primary" @click="retryLoad">重新加载</el-button>
          <el-button @click="goBack">返回上一页</el-button>
        </div>
      </div>

      <!-- 正常页面内容 -->
      <div v-else>
        <div class="course-header">
          <div class="course-title-section">
            <div class="course-title-with-cover">
              <div class="course-cover-section" v-if="course.image && course.image !== 'default'">
                <img :src="getCourseCoverUrl(course.image)" class="course-cover-large" alt="课程封面" @error="handleImageError">
              </div>
              <div class="course-info-section">
                <h1>{{ course.course_name }}</h1>
                <div class="course-meta">
                  <span class="meta-item"><i class="el-icon-user"></i> {{ course.teacher_name }}</span>
                  <span class="meta-item"><i class="el-icon-office-building"></i> {{ course.organization }}</span>
                  <span class="meta-item"><i class="el-icon-date"></i> {{ course.term }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      <div class="course-content">
        <div class="course-sidebar">
          <el-menu
            :default-active="activeMenu"
            class="course-menu"
            @select="handleMenuSelect">

            <el-menu-item v-if="isTeacher" index="teaching-plan">
              <i class="el-icon-date"></i>
              <span>教学计划</span>
            </el-menu-item>

            <el-menu-item v-if="!isTeacher" index="learning-plan">
              <i class="el-icon-date"></i>
              <span>学习计划</span>
            </el-menu-item>

            <el-menu-item index="inform">
              <i class="el-icon-document"></i>
              <span>课程通知</span>
            </el-menu-item>

            <el-menu-item index="profile">
              <i class="el-icon-document"></i>
              <span>课程简介</span>
            </el-menu-item>

            <el-menu-item v-if="isTeacher" index="target">
              <i class="el-icon-reading"></i>
              <span>教学目标</span>
            </el-menu-item>

            <el-menu-item index="chapter">
              <i class="el-icon-folder"></i>
              <span>课程章节</span>
            </el-menu-item>

            <el-menu-item index="knowledge">
              <i class="el-icon-collection"></i>
              <span>课程知识点</span>
            </el-menu-item>

            <el-menu-item v-if="isTeacher" index="class-management">
              <i class="el-icon-user"></i>
              <span>班级管理</span>
            </el-menu-item>
            <el-menu-item v-if="isTeacher" index="question-bank">
              <i class="el-icon-collection-tag"></i>
              <span>题库管理</span>
            </el-menu-item>
            <el-menu-item v-if="isTeacher" index="homework">
              <i class="el-icon-folder"></i>
              <span>作业管理</span>
            </el-menu-item>
             <el-menu-item v-if="!isTeacher" index="student-homework-summary">
              <i class="el-icon-folder"></i>
              <span>作业汇总</span>
            </el-menu-item>

            <el-menu-item index="resources">
              <i class="el-icon-folder"></i>
              <span>课程资料</span>
            </el-menu-item>

            <el-menu-item index="wrong-question-set">
              <i class="el-icon-folder"></i>
              <span>错题集</span>
            </el-menu-item>

            <el-menu-item v-if="isTeacher" index="record">
              <i class="el-icon-folder"></i>
              <span>学习记录</span>
            </el-menu-item>

            <!-- 教师端学情分析 放在讨论前面 -->
            <el-menu-item v-if="isTeacher" index="analysis">
              <i class="el-icon-data-analysis"></i>
              <span>学情分析</span>
            </el-menu-item>

            <!-- 学生端学习报告：按需求暂不对学生展示 -->
            <el-menu-item v-if="isTeacher" index="report">
              <i class="el-icon-document"></i>
              <span>学习报告</span>
            </el-menu-item>

            <el-menu-item index="discussion">
              <i class="el-icon-folder"></i>
              <span>讨论</span>
            </el-menu-item>

          </el-menu>
        </div>

        <div class="course-main">
          <!-- 教学计划 -->
          <div v-if="activeMenu === 'teaching-plan'" class="course-section">
            <TeachingPlan :course-id="Number(courseId)" />
          </div>

          <!-- 学习计划 -->
          <div v-if="activeMenu === 'learning-plan'" class="course-section">
            <LearningPlan v-if="courseId" :course-id="Number(courseId)" @go-to-homework="handleGoToHomework" />
            <!-- 调试信息 -->
            <div v-if="!courseId" style="padding: 20px; color: red;">
              调试: courseId为空, activeMenu={{ activeMenu }}
            </div>
          </div>

          <div v-if="activeMenu === 'inform'" class="course-section">
            <div class="notice-header">
              <h3>课程通知</h3>
              <!-- 教师角色显示管理按钮和添加按钮 -->
              <div v-if="isTeacher" class="notice-actions">
                <el-button
                  type="primary"
                  size="small"
                  icon="el-icon-plus"
                  @click="showAddNoticeDialog">添加课程通知</el-button>
                <el-button
                  type="primary"
                  size="small"
                  @click="goToNoticeManagement">管理课程通知</el-button>
              </div>
            </div>

            <!-- 加载状态 -->
            <div v-if="loadingStates.notices" class="loading-container"
                 v-loading="loadingStates.notices"
                  element-loading-text="正在加载课程通知..."
                 element-loading-spinner="el-icon-loading"
                 element-loading-background="rgba(255, 255, 255, 0.8)"
                 style="height: 200px;">
            </div>

            <!-- 课程通知列表 -->
            <div v-else-if="notices.length > 0" class="notice-list">
              <div v-for="notice in notices" :key="notice.id" class="notice-item">
                <div class="notice-item-header">
                  <h4 class="notice-title">{{ notice.title }}</h4>
                  <div class="notice-meta">
                    <span class="notice-time">{{ notice.createTime }}</span>
                  </div>
                </div>
                <div class="notice-content">{{ notice.content }}</div>
              </div>
            </div>

            <!-- 无课程通知时显示提示 -->
            <div v-else class="empty-tip">
              <i class="el-icon-bell"></i>
              <p>暂无课程通知</p>
            </div>

            <!-- 分页 -->
            <el-pagination
              v-if="total > 0"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pageNum"
              :page-sizes="[5, 10, 20, 50]"
              :page-size="pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="total">
            </el-pagination>
          </div>

          <div v-if="activeMenu === 'profile'" class="course-section">
            <div class="section-header">
              <h3>课程简介</h3>
              <el-button
                v-if="isTeacher"
                type="primary"
                size="small"
                icon="el-icon-edit"
                @click="showEditProfileDialog">编辑</el-button>
            </div>
            <div class="course-text">{{ course.profile || '暂无课程简介' }}</div>
          </div>

          <div v-if="activeMenu === 'target'" class="course-section">
            <div class="section-header">
              <h3>教学目标</h3>
              <el-button
                v-if="isTeacher"
                type="primary"
                size="small"
                icon="el-icon-edit"
                @click="showEditTargetDialog">编辑</el-button>
            </div>
            <div class="course-text">{{ course.target || '暂无教学目标' }}</div>
          </div>

          <div v-if="activeMenu === 'resources'" class="course-section">
            <div class="section-header">
              <h3>课程资料</h3>
              <el-button
                v-if="isTeacher"
                type="primary"
                size="small"
                icon="el-icon-upload"
                @click="showUploadDialog">上传资料</el-button>
            </div>

            <!-- 加载状态 -->
            <div v-if="loadingStates.resources" class="loading-container"
                 v-loading="loadingStates.resources"
                 element-loading-text="正在加载课程资料..."
                 element-loading-spinner="el-icon-loading"
                 element-loading-background="rgba(255, 255, 255, 0.8)"
                 style="height: 200px;">
            </div>

            <!-- 资料列表 -->
            <div v-else-if="courseResources.length > 0" class="resource-list">
              <div v-for="resource in courseResources" :key="resource.id" class="resource-item">
                <i :class="getFileIcon(resource.originalFileName)"></i>
                <div class="resource-info">
                  <div class="resource-name">{{ resource.originalFileName }}</div>
                  <div class="resource-meta">
                    <span class="resource-size">{{ formatFileSize(resource.fileSize) }}</span>
                    <span class="resource-downloads">下载 {{ resource.downloadCount || 0 }} 次</span>
                    <span class="resource-time">{{ formatTime(resource.createTime) }}</span>
                  </div>
                  <div v-if="resource.description" class="resource-description">{{ resource.description }}</div>
                </div>
                <div class="resource-actions">
                  <el-button
                    type="primary"
                    size="mini"
                    icon="el-icon-download"
                    @click="downloadResource(resource)">下载</el-button>
                  <el-button
                    v-if="isTeacher"
                    type="warning"
                    size="mini"
                    icon="el-icon-edit"
                    @click="editResource(resource)">编辑</el-button>
                  <el-button
                    v-if="isTeacher"
                    type="danger"
                    size="mini"
                    icon="el-icon-delete"
                    @click="deleteResource(resource)">删除</el-button>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-else class="empty-tip">
              <i class="el-icon-document"></i>
              <p>暂无课程资料</p>
              <el-button
                v-if="isTeacher"
                type="primary"
                @click="showUploadDialog">上传第一个资料</el-button>
            </div>
          </div>

          <div v-if="activeMenu === 'chapter'" class="course-section">
            <div class="section-header">
              <h3>章节</h3>
              <el-button
                v-if="isTeacher"
                type="primary"
                size="small"
                icon="el-icon-plus"
                @click="showAddChapterDialog">新增章节</el-button>
            </div>
            <div v-if="loadingStates.chapters" class="loading-container" v-loading="true" element-loading-text="正在加载章节..." element-loading-spinner="el-icon-loading" element-loading-background="rgba(255,255,255,0.8)" style="height: 200px;" />
            <div v-else>
              <div v-if="chapters.length===0" class="empty-tip">
                <i class="el-icon-folder"></i>
                <p>暂无章节</p>
              </div>
              <el-table v-else :data="chapters" size="small" border>
                <el-table-column label="#" type="index" width="60" />
                <el-table-column prop="title" label="标题" min-width="200" />
                <el-table-column prop="description" label="描述" min-width="260" />
                <el-table-column prop="sortOrder" label="排序" width="90" />
                <el-table-column label="知识点" min-width="260">
                  <template slot-scope="{ row }">
                    <div v-if="knowledgePoints && knowledgePoints.length">
                      <template v-if="knowledgePoints.filter(k => k.chapterId === row.id).length">
                        <el-tag
                          v-for="kp in knowledgePoints.filter(k => k.chapterId === row.id)"
                          :key="kp.id"
                          size="mini"
                          type="info"
                          style="margin-right: 4px; margin-bottom: 2px;"
                        >
                          {{ kp.name }}
                        </el-tag>
                      </template>
                      <span v-else style="color:#999;">无</span>
                    </div>
                    <span v-else style="color:#999;">—</span>
                  </template>
                </el-table-column>
                <el-table-column label="课程资源" min-width="260">
                  <template slot-scope="{ row }">
                    <div v-if="chapterResources && chapterResources[row.id] && chapterResources[row.id].length">
                      <el-tag
                        v-for="resource in chapterResources[row.id]"
                        :key="resource.id"
                        size="mini"
                        type="success"
                        style="margin-right: 4px; margin-bottom: 2px; cursor: pointer;"
                        :title="`点击下载: ${resource.description || resource.originalFileName}`"
                        @click="downloadResource(resource)"
                      >
                        <i class="el-icon-download" style="margin-right: 2px;"></i>
                        {{ resource.originalFileName }}
                      </el-tag>
                    </div>
                    <span v-else style="color:#999;">无</span>
                  </template>
                </el-table-column>
                <el-table-column v-if="isTeacher" label="操作" width="440">
                  <template slot-scope="{ row }">
                    <el-button size="mini" @click="editChapter(row)">编辑</el-button>
                    <el-button size="mini" type="primary" @click="manageChapterKnowledge(row)">管理知识点</el-button>
                    <el-button size="mini" type="success" @click="manageChapterResources(row)">管理资源</el-button>
                    <el-button size="mini" type="danger" @click="removeChapter(row)">删除</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>

          <div v-if="activeMenu === 'knowledge'" class="course-section">
            <div class="section-header">
              <h3>课程知识点</h3>
              <el-button
                v-if="isTeacher"
                type="primary"
                size="small"
                icon="el-icon-plus"
                @click="showAddKnowledgeDialog">新增知识点</el-button>
            </div>
            <div v-if="loadingStates.knowledge" class="loading-container" v-loading="true" element-loading-text="正在加载知识点..." element-loading-spinner="el-icon-loading" element-loading-background="rgba(255,255,255,0.8)" style="height: 200px;" />
            <div v-else>
              <div v-if="knowledgePoints.length===0" class="empty-tip">
                <i class="el-icon-collection"></i>
                <p>暂无知识点</p>
              </div>
              <el-table v-else :data="knowledgePoints" size="small" border>
                <el-table-column label="#" type="index" width="60" />
                <el-table-column prop="name" label="名称" min-width="200" />
                <el-table-column prop="description" label="描述" min-width="200" />
                <el-table-column prop="difficultyLevel" label="难度" width="80" />
                <el-table-column prop="sortOrder" label="排序" width="80" />
                <el-table-column label="所属章节" min-width="140">
                  <template slot-scope="{ row }">
                    {{ chapterNameById(row.chapterId) || '-' }}
                  </template>
                </el-table-column>
                <el-table-column label="子知识点" min-width="200">
                  <template slot-scope="{ row }">
                    <div v-if="row.children && row.children.length > 0" class="children-knowledge-list">
                      <el-tag
                        v-for="child in row.children"
                        :key="child.id"
                        size="mini"
                        type="info"
                        style="margin-right: 4px; margin-bottom: 2px;"
                      >
                        {{ child.name }}
                      </el-tag>
                    </div>
                    <span v-else style="color: #999;">无</span>
                  </template>
                </el-table-column>
                <el-table-column v-if="isTeacher" label="操作" width="360">
                  <template slot-scope="{ row }">
                    <el-button size="mini" @click="editKnowledge(row)">编辑</el-button>
                    <el-button size="mini" type="primary" @click="manageChildrenKnowledge(row)">管理子知识点</el-button>
                    <el-button size="mini" type="danger" @click="removeKnowledge(row)">删除</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>

          <div v-if="activeMenu === 'discussion'" class="course-section">
            <CourseDiscussion :course-id="courseId" :is-teacher="isTeacher" />
          </div>
          <div v-if="activeMenu === 'exam'" class="course-section">
            <ExamManagement v-if="isTeacher" :course-id="courseId" />
            <StudentExamInfo v-else :course-id="courseId" @view-exam-detail="openExamDetail" />
          </div>

          <div v-if="activeMenu === 'wrong-question-set'" class="course-section">
            <!-- 教师用户显示课程错题集 -->
            <WrongQuestionBook v-if="isTeacher" :course-id="courseId" />
            <!-- 学生用户显示个人错题集 -->
            <StudentWrongQuestionBook v-else :course-id="courseId" :student-id="currentStudentId" />
          </div>

          <div v-if="!isTeacher && activeMenu === 'record'" class="course-section">
            <h3>学习记录</h3>
            <div class="course-text">{{ '' }}</div>
          </div>

          <div v-if="activeMenu === 'class-management'" class="course-section">
            <CourseClassManagement
              v-if="isTeacher"
              :course-id="courseId"
              :is-teacher="isTeacher" />
            <div v-else class="empty-tip">
              <i class="el-icon-user"></i>
              <p>只有教师可以管理班级</p>
            </div>
          </div>
          <!-- 题库管理 -->
          <div v-if="activeMenu === 'question-bank'" class="course-section">
            <QuestionBankManagement :course-id="Number(courseId)" />
          </div>

          <div v-if="activeMenu === 'homework'" class="course-section">
            <!-- 加载状态 -->
            <div v-if="loadingStates.homework" class="loading-container"
                 v-loading="loadingStates.homework"
                 element-loading-text="正在加载作业数据..."
                 element-loading-spinner="el-icon-loading"
                 element-loading-background="rgba(255, 255, 255, 0.8)"
                 style="height: 200px;">
            </div>

            <div v-else-if="isTeacher">
              <CourseClassHomework
                :course-id="courseId"
                :is-teacher="isTeacher"
                @error="handleHomeworkComponentError" />
            </div>
            <div v-else>
              <p>显示学生作业汇总组件，isTeacher: {{ isTeacher }}</p>
              <StudentHomeworkSummary
                ref="homeworkComponent"
                :course-id="courseId" />
            </div>
          </div>

          <!-- 学生端作业汇总 -->
          <div v-if="activeMenu === 'student-homework-summary'" class="course-section">
            <StudentHomeworkSummary
              ref="homeworkComponent"
              :course-id="courseId" />
          </div>

          <!-- 教师端学情分析 -->
          <div v-if="activeMenu === 'analysis'" class="course-section">
            <CourseAnalysis
              v-if="isTeacher"
              :course-id="courseId" />
            <div v-else class="empty-tip">
              <i class="el-icon-data-analysis"></i>
              <p>只有教师可以查看学情分析</p>
            </div>
          </div>

          <!-- 学生端学习报告 -->
          <div v-if="activeMenu === 'report'" class="course-section">
            <StudentReport
              v-if="!isTeacher"
              :course-id="courseId" />
            <div v-else class="empty-tip">
              <i class="el-icon-document"></i>
              <p>只有学生可以查看学习报告</p>
            </div>
          </div>

        </div>
      </div>
      </div>

      <!-- 考试详情对话框 -->
      <el-dialog
        title="考试详情"
        :visible.sync="examDetailVisible"
        width="90%"
        :before-close="closeExamDetail"
        custom-class="exam-detail-dialog"
      >
        <StudentExamDetail
          v-if="currentExam"
          :exam-id="currentExam.id"
          :course-id="courseId"
          @close="closeExamDetail"
        />
      </el-dialog>

      <!-- 添加/编辑课程通知对话框 -->
      <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="50%">
        <el-form :model="noticeForm" :rules="rules" ref="noticeForm" label-width="80px">
          <el-form-item label="标题" prop="title">
            <el-input v-model="noticeForm.title" placeholder="请输入课程通知标题"></el-input>
          </el-form-item>
          <el-form-item label="内容" prop="content">
            <el-input type="textarea" v-model="noticeForm.content" :rows="6" placeholder="请输入课程通知内容"></el-input>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="dialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitNoticeForm">确 定</el-button>
        </span>
      </el-dialog>

      <!-- 添加/编辑章节对话框 -->
      <el-dialog title="章节" :visible.sync="chapterDialogVisible" width="480px">
        <el-form :model="chapterForm" label-width="80px">
          <el-form-item label="标题">
            <el-input v-model="chapterForm.title" placeholder="请输入章节标题" />
          </el-form-item>
          <el-form-item label="描述">
            <el-input type="textarea" :rows="4" v-model="chapterForm.description" placeholder="请输入描述" />
          </el-form-item>
          <el-form-item label="排序">
            <el-input v-model.number="chapterForm.sortOrder" type="number" min="0" />
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="chapterDialogVisible=false">取 消</el-button>
          <el-button type="primary" @click="saveChapter">保 存</el-button>
        </span>
      </el-dialog>

      <!-- 添加/编辑知识点对话框 -->
      <el-dialog title="知识点" :visible.sync="knowledgeDialogVisible" width="520px">
        <el-form :model="knowledgeForm" label-width="90px">
          <el-form-item label="名称">
            <el-input v-model="knowledgeForm.name" placeholder="请输入知识点名称" />
          </el-form-item>
          <el-form-item label="描述">
            <el-input type="textarea" :rows="4" v-model="knowledgeForm.description" placeholder="请输入描述" />
          </el-form-item>
          <el-form-item label="所属章节">
            <el-select v-model="knowledgeForm.chapterId" placeholder="可选择所属章节（可选）" filterable clearable style="width: 100%">
              <el-option v-for="c in chapters" :key="c.id" :label="c.title" :value="c.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="难度(1-5)">
            <el-input v-model.number="knowledgeForm.difficultyLevel" type="number" min="1" max="5" />
          </el-form-item>
          <el-form-item label="排序">
            <el-input v-model.number="knowledgeForm.sortOrder" type="number" min="0" />
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="knowledgeDialogVisible=false">取 消</el-button>
          <el-button type="primary" @click="saveKnowledge">保 存</el-button>
        </span>
      </el-dialog>

      <!-- 章节知识点管理对话框 -->
      <el-dialog :title="`管理章节知识点 - ${(currentChapter && currentChapter.title) || ''}`" :visible.sync="chapterKnowledgeDialogVisible" width="800px">
        <div class="chapter-knowledge-management">
          <div class="management-header">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="showAddKnowledgeToChapterDialog">新建知识点</el-button>
            <el-button type="success" size="small" icon="el-icon-link" @click="showLinkKnowledgeToChapterDialog">关联已有知识点</el-button>
            <el-button size="small" @click="refreshChapterKnowledge">刷新</el-button>
          </div>

          <el-table :data="currentChapterKnowledge" size="small" border style="margin-top: 15px;">
            <el-table-column label="#" type="index" width="60" />
            <el-table-column prop="name" label="知识点名称" min-width="200" />
            <el-table-column prop="description" label="描述" min-width="260" />
            <el-table-column prop="difficultyLevel" label="难度" width="80" />
            <el-table-column prop="sortOrder" label="排序" width="80" />
            <el-table-column label="操作" width="160">
              <template slot-scope="{ row }">
                <el-button size="mini" @click="editKnowledgeInChapter(row)">编辑</el-button>
                <el-button size="mini" type="danger" @click="removeKnowledgeFromChapter(row)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="chapterKnowledgeDialogVisible=false">关 闭</el-button>
        </span>
      </el-dialog>

      <!-- 关联已有知识点对话框 -->
      <el-dialog title="关联已有知识点到此章节" :visible.sync="linkKnowledgeDialogVisible" width="600px">
        <div class="link-knowledge-section">
          <p>选择要关联到此章节的知识点：</p>
          <el-table :data="availableKnowledgePoints" size="small" border @selection-change="handleKnowledgeSelectionChange">
            <el-table-column type="selection" width="55" />
            <el-table-column prop="name" label="知识点名称" min-width="200" />
            <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
            <el-table-column prop="difficultyLevel" label="难度" width="80" />
            <el-table-column label="当前章节" width="120">
              <template slot-scope="{ row }">
                <span v-if="row.chapterId">{{ chapterNameById(row.chapterId) }}</span>
                <span v-else style="color: #999;">未分配</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="linkKnowledgeDialogVisible=false">取 消</el-button>
          <el-button type="primary" @click="linkSelectedKnowledgeToChapter" :disabled="selectedKnowledgePoints.length === 0">确 定</el-button>
        </span>
      </el-dialog>

      <!-- 编辑课程简介对话框 -->
      <el-dialog title="编辑课程简介" :visible.sync="profileDialogVisible" width="50%">
        <el-form :model="courseForm" ref="profileForm" label-width="80px">
          <el-form-item label="课程简介">
            <el-input type="textarea" v-model="courseForm.profile" :rows="8" placeholder="请输入课程简介"></el-input>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="profileDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitProfileForm">确 定</el-button>
        </span>
      </el-dialog>

      <!-- 编辑教学目标对话框 -->
      <el-dialog title="编辑教学目标" :visible.sync="targetDialogVisible" width="50%">
        <el-form :model="courseForm" ref="targetForm" label-width="80px">
          <el-form-item label="教学目标">
            <el-input type="textarea" v-model="courseForm.target" :rows="8" placeholder="请输入教学目标"></el-input>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="targetDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitTargetForm">确 定</el-button>
        </span>
      </el-dialog>

      <!-- 上传资料对话框 -->
      <el-dialog title="上传课程资料" :visible.sync="uploadDialogVisible" width="50%">
        <el-form :model="uploadForm" :rules="uploadRules" ref="uploadForm" label-width="100px">
          <el-form-item label="选择文件" prop="file">
            <el-upload
              ref="upload"
              action=""
              :auto-upload="false"
              :on-change="handleFileChange"
              :file-list="fileList"
              :limit="1"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.zip,.rar,.7z,.mp4,.avi,.mov,.wmv,.flv,.webm,.mkv"
            >
              <el-button size="small" type="primary">选择文件</el-button>
              <div slot="tip" class="el-upload__tip">
                支持PDF、Word、PPT、压缩包和视频文件（mp4/avi/mov/wmv/flv/webm/mkv），文件大小不超过500MB
              </div>
            </el-upload>
          </el-form-item>
          <el-form-item label="资料描述">
            <el-input
              v-model="uploadForm.description"
              type="textarea"
              :rows="3"
              placeholder="请输入资料描述（可选）"
            />
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="uploadDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitUpload" :loading="uploading">上 传</el-button>
        </span>
      </el-dialog>

      <!-- 编辑资料对话框 -->
      <el-dialog title="编辑课程资料" :visible.sync="editDialogVisible" width="50%">
        <el-form :model="editForm" ref="editForm" label-width="100px">
          <el-form-item label="文件名">
            <el-input v-model="editForm.originalFileName" disabled />
          </el-form-item>
          <el-form-item label="资料描述">
            <el-input
              v-model="editForm.description"
              type="textarea"
              :rows="3"
              placeholder="请输入资料描述"
            />
          </el-form-item>
          <el-form-item label="替换文件">
            <el-upload
              ref="editUpload"
              action=""
              :auto-upload="false"
              :on-change="handleEditFileChange"
              :file-list="editFileList"
              :limit="1"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.zip,.rar,.7z,.mp4,.avi,.mov,.wmv,.flv,.webm,.mkv"
            >
              <el-button size="small" type="primary">选择新文件</el-button>
              <div slot="tip" class="el-upload__tip">
                选择新文件将替换当前文件
              </div>
            </el-upload>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="editDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitEdit" :loading="editing">保 存</el-button>
        </span>
      </el-dialog>

      <!-- 子知识点管理对话框 -->
      <el-dialog :title="`管理子知识点 - ${(currentParentKnowledge && currentParentKnowledge.name) || ''}`" :visible.sync="childrenKnowledgeDialogVisible" width="900px">
        <div class="children-knowledge-management">
          <div class="management-header">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="showAddChildKnowledgeDialog">添加子知识点</el-button>
            <el-button size="small" @click="refreshChildrenKnowledge">刷新</el-button>
          </div>

          <el-table :data="currentChildrenKnowledge" size="small" border style="margin-top: 15px;">
            <el-table-column label="#" type="index" width="60" />
            <el-table-column prop="name" label="子知识点名称" min-width="200" />
            <el-table-column prop="description" label="描述" min-width="260" />
            <el-table-column prop="difficultyLevel" label="难度" width="80" />
            <el-table-column prop="sortOrder" label="排序" width="80" />
            <el-table-column label="操作" width="160">
              <template slot-scope="{ row }">
                <el-button size="mini" @click="editChildKnowledge(row)">编辑</el-button>
                <el-button size="mini" type="danger" @click="removeChildKnowledge(row)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="childrenKnowledgeDialogVisible=false">关 闭</el-button>
        </span>
      </el-dialog>

      <!-- 添加子知识点对话框 -->
      <el-dialog title="添加子知识点" :visible.sync="addChildKnowledgeDialogVisible" width="600px">
        <el-form :model="childKnowledgeForm" label-width="100px">
          <el-form-item label="选择知识点">
            <el-select v-model="childKnowledgeForm.childId" placeholder="请选择要添加为子知识点的知识点" style="width: 100%">
              <el-option
                v-for="kp in availableKnowledgePoints"
                :key="kp.id"
                :label="kp.name"
                :value="kp.id"
                :disabled="kp.id === (currentParentKnowledge && currentParentKnowledge.id)"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="关系类型">
            <el-radio-group v-model="childKnowledgeForm.relationType">
              <el-radio :label="1">父子关系</el-radio>
              <el-radio :label="2">前置关系</el-radio>
              <el-radio :label="3">相关关系</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="addChildKnowledgeDialogVisible=false">取 消</el-button>
          <el-button type="primary" @click="saveChildKnowledge">确 定</el-button>
        </span>
      </el-dialog>

      <!-- 章节资源管理对话框 -->
      <el-dialog :title="`管理章节资源 - ${(currentChapter && currentChapter.title) || ''}`" :visible.sync="chapterResourceDialogVisible" width="900px">
        <div class="chapter-resource-management">
          <div class="management-header">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="showAddResourceToChapterDialog">关联资源</el-button>
            <el-button size="small" @click="refreshChapterResources">刷新</el-button>
          </div>

          <el-table :data="currentChapterResources" size="small" border style="margin-top: 15px;">
            <el-table-column label="#" type="index" width="60" />
            <el-table-column prop="originalFileName" label="文件名" min-width="200" />
            <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
            <el-table-column prop="fileSize" label="文件大小" width="100">
              <template slot-scope="{ row }">
                {{ formatFileSize(row.fileSize) }}
              </template>
            </el-table-column>
            <el-table-column prop="downloadCount" label="下载次数" width="100" />
            <el-table-column label="操作" width="160">
              <template slot-scope="{ row }">
                <el-button size="mini" type="primary" @click="downloadResource(row)">下载</el-button>
                <el-button size="mini" type="danger" @click="removeResourceFromChapter(row)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="chapterResourceDialogVisible=false">关 闭</el-button>
        </span>
      </el-dialog>

      <!-- 关联资源到章节对话框 -->
      <el-dialog title="关联资源到此章节" :visible.sync="linkResourceDialogVisible" width="700px">
        <div class="link-resource-section">
          <p>选择要关联到此章节的课程资源：</p>
          <el-table :data="availableCourseResources" size="small" border @selection-change="handleResourceSelectionChange">
            <el-table-column type="selection" width="55" />
            <el-table-column prop="originalFileName" label="文件名" min-width="200" />
            <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
            <el-table-column prop="fileSize" label="文件大小" width="100">
              <template slot-scope="{ row }">
                {{ formatFileSize(row.fileSize) }}
              </template>
            </el-table-column>
            <el-table-column prop="downloadCount" label="下载次数" width="100" />
          </el-table>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="linkResourceDialogVisible=false">取 消</el-button>
          <el-button type="primary" @click="linkSelectedResourcesToChapter" :disabled="selectedResources.length === 0">确 定</el-button>
        </span>
      </el-dialog>
    </div>
  </template>

  <script>
  import { getCourseById, getCourseNotices, getNewCourseNotices, addCourseNotice, updateCourseNotice, deleteCourseNotice, updateCourse } from '@/api/course'
  import {
    getCourseResourceList,
    uploadCourseResource,
    downloadCourseResource,
    updateCourseResource,
    deleteCourseResource
  } from '@/api/courseResource'
  import { getHomeworkListByCourseId } from '@/api/homework'
  import { getRole, getTokenInfo } from '@/utils/jwtUtils'
  import CourseClassManagement from '@/components/CourseClassManagement'
  import CourseClassHomework from '@/components/CourseClassHomework'
  import CourseAnalysis from '@/components/CourseAnalysis'
  import StudentReport from '@/components/StudentReport'
  import StudentHomeworkSummary from '@/components/StudentHomeworkSummary'
  import WrongQuestionBook from '@/components/WrongQuestionBook.vue'
import StudentWrongQuestionBook from '@/components/StudentWrongQuestionBook.vue'
import CourseDiscussion from '@/components/CourseDiscussion.vue'
import QuestionBankManagement from './QuestionBankManagement.vue'
import ExamManagement from './exam/ExamManagement.vue'
import StudentExamInfo from '@/components/StudentExamInfo'
import StudentExamDetail from '@/components/StudentExamDetail'
import TeachingPlan from '@/components/TeachingPlan'
import LearningPlan from '@/components/LearningPlan'
  import { getChaptersByCourse, createChapter, updateChapter, deleteChapter } from '@/api/chapter'
  import { getKnowledgeByCourse, getKnowledgeByChapter, createKnowledge, updateKnowledge, deleteKnowledge, getChildrenByParentId, addChildKnowledgePoint, removeChildKnowledgePoint } from '@/api/knowledge'
  import {
    getResourcesByChapter,
    associateResourceToChapter,
    disassociateResourceFromChapter,
    batchAssociateResourcesToChapter,
    getChapterResourcesWithDetails
  } from '@/api/chapterResource'

  export default {
    name: 'CourseDetail',
    components: {
      CourseClassManagement,
      CourseClassHomework,
      CourseAnalysis,
      StudentReport,
      StudentHomeworkSummary,
      WrongQuestionBook,
      StudentWrongQuestionBook,
      CourseDiscussion,
      QuestionBankManagement,
      ExamManagement,
      StudentExamInfo,
      StudentExamDetail,
      TeachingPlan,
      LearningPlan
    },
    data() {
      return {
        courseId: null,
        course: {},
        isTeacher: false,
        isSelected: false,
        courseResources: [],
        activeMenu: 'teaching-plan',
        pageLoading: true,
        pageError: false,
        // 通知相关数据
        notices: [],
        total: 0,
        pageNum: 1,
        pageSize: 10,
        dialogVisible: false,
        dialogTitle: '添加课程通知',
        noticeForm: {
          courseId: '',
          title: '',
          content: ''
        },
        // 课程简介和教学目标编辑相关数据
        profileDialogVisible: false,
        targetDialogVisible: false,
        courseForm: {
          id: null,
          profile: '',
          target: ''
        },
        editingNoticeId: null,
        rules: {
          title: [
            { required: true, message: '请输入课程通知标题', trigger: 'blur' },
            { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
          ],
          content: [
            { required: true, message: '请输入课程通知内容', trigger: 'blur' }
          ]
        },
        userId: null,
        currentStudentId: null,
        // 课程资料相关数据
        uploadDialogVisible: false,
        editDialogVisible: false,
        uploading: false,
        editing: false,
        uploadForm: {
          file: null,
          description: ''
        },
        editForm: {
          id: null,
          originalFileName: '',
          description: '',
          file: null
        },
        fileList: [],
        editFileList: [],
        uploadRules: {
          file: [
            { required: true, message: '请选择文件', trigger: 'change' }
          ]
        },
        // 作业管理相关数据
        homeworkList: [],
        homeworkLoading: false,
        // 新增：各模块加载状态管理
        loadingStates: {
          notices: false,
          resources: false,
          homework: false,
          chapters: false,
          knowledge: false
        },
        // 新增：数据缓存标记
        dataLoaded: {
          notices: false,
          resources: false,
          homework: false,
          chapters: false,
          knowledge: false
        },
        // 考试详情相关
        examDetailVisible: false,
        currentExam: null,
        // 章节与知识点数据
        chapters: [],
        knowledgePoints: [],
        // 弹窗与表单
        chapterDialogVisible: false,
        chapterForm: { id: null, title: '', description: '', sortOrder: 0 },
        knowledgeDialogVisible: false,
        knowledgeForm: { id: null, name: '', description: '', difficultyLevel: 1, sortOrder: 0, chapterId: null },
        // 章节知识点管理
        chapterKnowledgeDialogVisible: false,
        currentChapter: null,
        currentChapterKnowledge: [],
        // 关联已有知识点
        linkKnowledgeDialogVisible: false,
        selectedKnowledgePoints: [],
        // 子知识点管理
        childrenKnowledgeDialogVisible: false,
        addChildKnowledgeDialogVisible: false,
        currentParentKnowledge: null,
        currentChildrenKnowledge: [],
        availableKnowledgePoints: [],
        childKnowledgeForm: { childId: null, relationType: 1 },
        // 章节资源管理
        chapterResourceDialogVisible: false,
        linkResourceDialogVisible: false,
        currentChapterResources: [],
        availableCourseResources: [],
        selectedResources: [],
        chapterResources: {}, // 存储每个章节关联的资源，key为章节ID
        _hasShownError: false
      }
    },
    async created() {
      console.log('课程详情页面 - 组件创建，开始初始化')
      console.log('课程详情页面 - 路由信息:', this.$route)
      console.log('课程详情页面 - 路由参数:', this.$route.params)
      console.log('课程详情页面 - 当前URL:', window.location.href)
      console.log('课程详情页面 - 用户信息:', localStorage.getItem('userInfo'))

      // 从路由参数获取课程ID
      this.courseId = this.$route.params.id
      console.log('课程详情页面 - 课程ID:', this.courseId)

      // 先检查用户角色（只调用一次）
      console.log('课程详情页面 - 开始检查用户角色')
      await this.checkUserRole()
      console.log('课程详情页面 - 用户角色检查完成')
      console.log('课程详情页面 - 是否为教师:', this.isTeacher)

      // 学生可访问的页签白名单
      const studentAllowedTabs = [
        'learning-plan',
        'inform',
        'profile',
        'chapter',
        'knowledge',
        'student-homework-summary',
        'exam',
        'resources',
        'wrong-question-set',
        'discussion'
      ]

      // 根据角色与query参数决定默认激活菜单
      if (this.isTeacher) {
        // 教师：允许通过query.tab指定
        if (this.$route.query.tab && !this.$route.query.homeworkId) {
          this.activeMenu = this.$route.query.tab
          console.log('课程详情页面 - 从query参数设置默认激活菜单(教师):', this.activeMenu)
        } else {
          this.activeMenu = 'teaching-plan'
          console.log('课程详情页面 - 设置默认激活菜单(教师):', this.activeMenu)
        }
      } else {
        // 学生：默认学习计划；若query.tab在白名单中则使用
        const tabFromQuery = this.$route.query.tab
        if (tabFromQuery && studentAllowedTabs.includes(tabFromQuery) && !this.$route.query.homeworkId) {
          this.activeMenu = tabFromQuery
          console.log('课程详情页面 - 从query参数设置默认激活菜单(学生):', this.activeMenu)
        } else {
          this.activeMenu = 'learning-plan'
          console.log('课程详情页面 - 设置默认激活菜单(学生):', this.activeMenu)
        }
        // 清理不允许的tab参数
        if (this.$route.query.tab && !studentAllowedTabs.includes(this.$route.query.tab)) {
          const newQuery = { ...this.$route.query }
          delete newQuery.tab
          delete newQuery.homeworkId
          if (Object.keys(newQuery).length > 0) {
            this.$router.replace({ path: this.$route.path, query: newQuery })
          } else {
            this.$router.replace({ path: this.$route.path })
          }
        }
      }

      console.log('课程详情页面 - 最终activeMenu值:', this.activeMenu)
      console.log('课程详情页面 - v-show条件 (activeMenu === "learning-plan"):', this.activeMenu === 'learning-plan')

      if (this.courseId) {
        try {
          // 获取课程详情
          console.log('课程详情页面 - 开始获取课程详情')
          await this.fetchCourseDetail()
          console.log('课程详情页面 - 课程详情获取完成')

          // 隐藏侧边栏和导航栏
          console.log('课程详情页面 - 开始隐藏侧边栏和导航栏')
          this.hideSidebarAndNavbar()
          console.log('课程详情页面 - 侧边栏和导航栏隐藏完成')

          console.log('课程详情页面 - 初始化完成')
          this.pageLoading = false
        } catch (error) {
          console.error('课程详情页面 - 初始化失败:', error)
          // 静默处理，不显示错误页和弹窗
          this.pageLoading = false
        }
      } else {
        console.error('课程详情页面 - 课程ID不存在')
        // 静默返回，不弹窗
        this.goBack()
      }
    },
    watch: {
      activeMenu(newVal) {
        // 实现按需加载，只在用户点击对应导航时加载数据
        this.loadModuleData(newVal)

        // 如果切换到作业标签且有homeworkId参数，打开作业详情
        if (newVal === 'homework' && this.$route.query.homeworkId) {
          this.$nextTick(() => {
            this.openHomeworkById(this.$route.query.homeworkId)
          })
        }
      },
      isTeacher(newVal, oldVal) {
        console.log('课程详情页面 - isTeacher 值变化:', oldVal, '->', newVal)
        if (newVal === false && oldVal === true) {
          console.error('⚠️ isTeacher 从 true 变为 false，这可能导致404错误！')
        }
      }
    },
    beforeDestroy() {
      // 组件销毁前恢复侧边栏和导航栏显示
      this.showSidebarAndNavbar()
    },
    methods: {
      // 新增：按需加载模块数据
      async loadModuleData(moduleName) {
        // 如果数据已经加载过，直接返回
        if (this.dataLoaded[moduleName]) {
          console.log(`模块 ${moduleName} 数据已缓存，跳过加载`)
          return
        }

        // 设置加载状态
        this.loadingStates[moduleName] = true

        try {
          switch (moduleName) {
            case 'inform':
              await this.fetchNotices()
              break
            case 'resources':
              await this.fetchCourseResources()
              break
            case 'homework':
              await this.fetchHomeworkList()
              break
            case 'chapter':
              // 为了在章节表中展示知识点标签，这里确保已加载课程知识点
              if (!this.dataLoaded.knowledge) {
                try { await this.fetchKnowledgePoints() } catch (e) { console.error('预加载知识点失败:', e) }
                this.dataLoaded.knowledge = true
              }
              await this.fetchChapters()
              break
            case 'knowledge':
              await this.fetchKnowledgePoints()
              break
            case 'wrong-question-set':
              // 错题集组件自己处理数据加载，这里不需要额外操作
              console.log('错题集模块，组件将自行加载数据')
              break
            default:
              console.log(`模块 ${moduleName} 无需加载数据`)
          }

          // 标记数据已加载
          this.dataLoaded[moduleName] = true
          console.log(`模块 ${moduleName} 数据加载完成`)
        } catch (error) {
          console.error(`模块 ${moduleName} 数据加载失败:`, error)
          this.$message.error(`${this.getModuleDisplayName(moduleName)}加载失败`)
        } finally {
          // 清除加载状态
          this.loadingStates[moduleName] = false
        }
      },

      // 新增：获取模块显示名称
      getModuleDisplayName(moduleName) {
        const nameMap = {
          'inform': '课程通知',
          'resources': '课程资料',
          'homework': '作业管理',
          'chapter': '课程章节',
          'knowledge': '课程知识点'
        }
        return nameMap[moduleName] || moduleName
      },

      // 新增：强制刷新模块数据
      async refreshModuleData(moduleName) {
        // 清除缓存标记
        this.dataLoaded[moduleName] = false
        // 重新加载数据
        await this.loadModuleData(moduleName)
      },

      // ============ 章节相关 ============
      async fetchChapters() {
        const res = await getChaptersByCourse(this.courseId)
        if (res && res.code === 1) {
          this.chapters = res.data || []
          // 加载每个章节的资源
          await this.loadChapterResources()
        } else {
          throw new Error((res && res.msg) || '获取章节失败')
        }
      },
      showAddChapterDialog() {
        this.chapterDialogVisible = true
        this.chapterForm = { id: null, title: '', description: '', sortOrder: 0 }
      },
      async saveChapter() {
        const payload = { ...this.chapterForm, courseId: parseInt(this.courseId) }
        if (!this.chapterForm.id && this.userId) {
          payload.creatorId = this.userId
        }
        const api = this.chapterForm.id ? updateChapter.bind(null, this.chapterForm.id) : createChapter
        const res = await api(payload)
        if (res && res.code === 1) {
          this.$message.success('保存成功')
          this.chapterDialogVisible = false
          this.refreshModuleData('chapter')
        } else {
          this.$message.error((res && res.msg) || '保存失败')
        }
      },
      editChapter(row) {
        this.chapterDialogVisible = true
        this.chapterForm = { id: row.id, title: row.title, description: row.description, sortOrder: row.sortOrder }
      },
      async removeChapter(row) {
        await this.$confirm('确认删除该章节？', '提示')
        const res = await deleteChapter(row.id)
        if (res && res.code === 1) {
          this.$message.success('删除成功')
          this.refreshModuleData('chapter')
        } else {
          this.$message.error((res && res.msg) || '删除失败')
        }
      },

      // ============ 知识点相关 ============
      async fetchKnowledgePoints() {
        const res = await getKnowledgeByCourse(this.courseId)
        if (res && res.code === 1) {
          this.knowledgePoints = res.data || []
        } else {
          throw new Error((res && res.msg) || '获取知识点失败')
        }
      },
      showAddKnowledgeDialog() {
        this.knowledgeDialogVisible = true
        this.knowledgeForm = { id: null, name: '', description: '', difficultyLevel: 1, sortOrder: 0, chapterId: null }
      },
      async saveKnowledge() {
        const payload = { ...this.knowledgeForm, courseId: parseInt(this.courseId) }
        const api = this.knowledgeForm.id ? updateKnowledge.bind(null, this.knowledgeForm.id) : createKnowledge
        const res = await api(payload)
        if (res && res.code === 1) {
          this.$message.success('保存成功')
          this.knowledgeDialogVisible = false
          this.refreshModuleData('knowledge')
          // 如果是从章节管理对话框打开的，也要刷新章节知识点列表
          if (this.chapterKnowledgeDialogVisible && this.currentChapter) {
            await this.refreshChapterKnowledge()
          }
        } else {
          this.$message.error((res && res.msg) || '保存失败')
        }
      },
      editKnowledge(row) {
        this.knowledgeDialogVisible = true
        this.knowledgeForm = { id: row.id, name: row.name, description: row.description, difficultyLevel: row.difficultyLevel, sortOrder: row.sortOrder, chapterId: row.chapterId }
      },
      async removeKnowledge(row) {
        await this.$confirm('确认删除该知识点？', '提示')
        const res = await deleteKnowledge(row.id)
        if (res && res.code === 1) {
          this.$message.success('删除成功')
          this.refreshModuleData('knowledge')
        } else {
          this.$message.error((res && res.msg) || '删除失败')
        }
      },
      chapterNameById(chapterId) {
        const c = this.chapters.find(x => x.id === chapterId)
        return c && c.title
      },

      // ============ 章节知识点管理相关 ============
      async manageChapterKnowledge(chapter) {
        this.currentChapter = chapter
        this.chapterKnowledgeDialogVisible = true
        await this.fetchChapterKnowledge(chapter.id)
      },
      async fetchChapterKnowledge(chapterId) {
        const res = await getKnowledgeByChapter(chapterId)
        if (res && res.code === 1) {
          this.currentChapterKnowledge = res.data || []
        } else {
          this.$message.error((res && res.msg) || '获取章节知识点失败')
        }
      },
      showAddKnowledgeToChapterDialog() {
        this.knowledgeForm = {
          id: null,
          name: '',
          description: '',
          difficultyLevel: 1,
          sortOrder: 0,
          chapterId: this.currentChapter.id
        }
        this.knowledgeDialogVisible = true
      },
      async refreshChapterKnowledge() {
        if (this.currentChapter) {
          await this.fetchChapterKnowledge(this.currentChapter.id)
        }
      },
      editKnowledgeInChapter(row) {
        this.knowledgeForm = {
          id: row.id,
          name: row.name,
          description: row.description,
          difficultyLevel: row.difficultyLevel,
          sortOrder: row.sortOrder,
          chapterId: row.chapterId
        }
        this.knowledgeDialogVisible = true
      },
      async removeKnowledgeFromChapter(row) {
        await this.$confirm('确认从该章节移除该知识点？', '提示')
        const res = await deleteKnowledge(row.id)
        if (res && res.code === 1) {
          this.$message.success('移除成功')
          await this.refreshChapterKnowledge()
        } else {
          this.$message.error((res && res.msg) || '移除失败')
        }
      },

      // ============ 关联已有知识点相关 ============
      async showLinkKnowledgeToChapterDialog() {
        // 确保已加载课程知识点数据
        if (!this.dataLoaded.knowledge) {
          await this.fetchKnowledgePoints()
        }

        // 过滤出可以关联的知识点（排除已经属于当前章节的知识点）
        this.availableKnowledgePoints = this.knowledgePoints.filter(kp =>
          kp.chapterId !== this.currentChapter.id
        )

        this.selectedKnowledgePoints = []
        this.linkKnowledgeDialogVisible = true
      },

      handleKnowledgeSelectionChange(selection) {
        this.selectedKnowledgePoints = selection
      },

      async linkSelectedKnowledgeToChapter() {
        if (this.selectedKnowledgePoints.length === 0) {
          this.$message.warning('请选择要关联的知识点')
          return
        }

        try {
          // 批量更新选中知识点的章节ID
          const updatePromises = this.selectedKnowledgePoints.map(kp =>
            updateKnowledge(kp.id, { ...kp, chapterId: this.currentChapter.id })
          )

          await Promise.all(updatePromises)

          this.$message.success(`成功关联 ${this.selectedKnowledgePoints.length} 个知识点到此章节`)
          this.linkKnowledgeDialogVisible = false

          // 刷新相关数据
          await this.refreshChapterKnowledge()
          await this.refreshModuleData('knowledge')
        } catch (error) {
          console.error('关联知识点失败:', error)
          this.$message.error('关联知识点失败')
        }
      },

      // 获取课程封面URL
      getCourseCoverUrl(imageUrl) {
        const { getCourseCoverUrl } = require('@/utils/minio')
        return getCourseCoverUrl(imageUrl)
      },

      // 处理图片加载错误
      handleImageError(event) {
        console.warn('课程封面加载失败:', event.target.src)
        event.target.style.display = 'none'
      },

      // 获取课程详情
      async fetchCourseDetail() {
        try {
          // 先检查用户角色和ID
          await this.checkUserRole()

          console.log('课程详情页面 - 用户角色:', this.isTeacher ? '教师' : '学生')
          console.log('课程详情页面 - 用户ID:', this.userId)
          console.log('课程详情页面 - 课程ID:', this.courseId)

          // 检查认证信息
          const token = this.$store.getters.token
          const userInfo = localStorage.getItem('userInfo')
          console.log('课程详情页面 - Token存在:', !!token)
          console.log('课程详情页面 - UserInfo存在:', !!userInfo)
          console.log('课程详情页面 - Token值:', token ? token.substring(0, 20) + '...' : 'null')

          // 后端通过JWT token自动获取用户信息，不需要传递userId参数
          console.log('开始调用课程详情API，URL:', `/api/teachcourse/${this.courseId}`)

          // 测试API调用前先检查请求头
          const axios = require('axios')
          const testConfig = {
            url: `${process.env.VUE_APP_BASE_API}/teachcourse/${this.courseId}`,
            method: 'get',
            headers: {
              'Authorization': token
            }
          }
          console.log('测试请求配置:', testConfig)

          const response = await getCourseById(this.courseId)
          console.log('课程详情API响应:', response)
          if (response && response.code === 1 && response.data) {
            this.course = response.data
          } else {
            // 静默处理错误：不提示，不展示错误页，仅结束加载
            this.pageLoading = false
            return
          }
        } catch (error) {
          console.error('获取课程详情失败:', error)
          // 静默处理错误：不提示，不展示错误页，仅结束加载
          this.pageLoading = false
        }
      },

      // 返回上一页
      goBack() {
        // 如果历史记录中有页面，则返回上一页
        if (window.history.length > 1) {
          this.$router.go(-1)
        } else {
          // 否则跳转到课程管理页面
          this.$router.push('/course-management')
        }
      },

      // 处理菜单选择
      handleMenuSelect(key) {
        if (!this.isTeacher) {
          const studentAllowedTabs = [
            'learning-plan',
            'inform',
            'profile',
            'chapter',
            'knowledge',
            'student-homework-summary',
            'exam',
            'resources',
            'wrong-question-set',
            'discussion'
          ]
          if (!studentAllowedTabs.includes(key)) {
            // 不允许的直接忽略
            return
          }
        }
        this.activeMenu = key
      },

      // 处理跳转到作业汇总
      handleGoToHomework() {
        console.log('跳转到作业汇总页面')
        this.activeMenu = this.isTeacher ? 'homework' : 'student-homework-summary'
      },

      // 隐藏侧边栏和导航栏
      hideSidebarAndNavbar() {
        // 使用store修改sidebar的状态
        this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })

        // 隐藏navbar和sidebar元素
        const sidebar = document.querySelector('.sidebar-container')
        const navbar = document.querySelector('.navbar')

        if (sidebar) {
          sidebar.style.display = 'none'
        }

        if (navbar) {
          navbar.style.display = 'none'
        }

        // 调整主内容区域的样式
        const appMain = document.querySelector('.app-main')
        if (appMain) {
          appMain.style.padding = '0'
          appMain.style.margin = '0'
        }
      },
      // 跳转到课程通知管理页面
      goToNoticeManagement() {
        // 打开新窗口或跳转到课程通知管理页面
        this.$router.push({
          path: '/course/notice-management',
          query: { courseId: this.courseId }
        })
      },

      // 恢复侧边栏和导航栏显示
      showSidebarAndNavbar() {
        // 恢复sidebar的状态
        this.$store.dispatch('app/openSideBar', { withoutAnimation: false })

        // 显示navbar和sidebar元素
        const sidebar = document.querySelector('.sidebar-container')
        const navbar = document.querySelector('.navbar')

        if (sidebar) {
          sidebar.style.display = ''
        }

        if (navbar) {
          navbar.style.display = ''
        }

        // 恢复主内容区域的样式
        const appMain = document.querySelector('.app-main')
        if (appMain) {
          appMain.style.padding = ''
          appMain.style.margin = ''
        }
      },

      // 检查用户角色
      checkUserRole() {
        try {
          console.log('课程详情页面 - 开始检查用户角色')

          // 检查token是否存在
          const token = this.$store.getters.token || localStorage.getItem('token')
          console.log('课程详情页面 - Token存在:', !!token)

          if (!token) {
            console.warn('课程详情页面 - 未找到token，使用默认设置')
            this.isTeacher = false
            this.userId = null
            return
          }

          // 从JWT中获取用户角色
          const roleId = getRole()
          console.log('课程详情页面 - 获取到角色ID:', roleId)

          // 如果是教师角色(roleId 2或4)，设置isTeacher为true
          this.isTeacher = roleId === 2 || roleId === 4
          console.log('课程详情页面 - 是否为教师:', this.isTeacher)
          console.log('课程详情页面 - roleId:', roleId, 'isTeacher:', this.isTeacher)

          // 获取用户信息
          const userInfo = getTokenInfo()
          console.log('课程详情页面 - 获取到用户信息:', userInfo)

          if (userInfo) {
            this.userId = userInfo.id
            console.log('课程详情页面 - 用户ID:', this.userId)

            // 如果是学生，获取学生ID
            if (!this.isTeacher && roleId === 1) {
              // 从localStorage获取学生ID
              const localUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
              this.currentStudentId = localUserInfo.studentId || localUserInfo.data?.studentId || null

              // 如果没有学生ID，使用用户ID作为学生ID
              if (!this.currentStudentId) {
                this.currentStudentId = this.userId
              }

              console.log('课程详情页面 - 学生ID:', this.currentStudentId)
            }
          } else {
            console.error('课程详情页面 - 无法获取用户信息')
          }

          // 额外调试信息
          console.log('课程详情页面 - localStorage userInfo:', localStorage.getItem('userInfo'))
          console.log('课程详情页面 - localStorage roles:', localStorage.getItem('roles'))
          console.log('课程详情页面 - 最终isTeacher值:', this.isTeacher)
        } catch (error) {
          console.error('获取用户角色失败:', error)
          // 如果JWT解析失败，使用默认设置
          this.isTeacher = false
          this.userId = null
        }
      },

      // 获取课程通知列表
      async fetchNotices() {
        const fetchFunction = this.isTeacher ? getCourseNotices : getNewCourseNotices

        // 添加userId参数
        const params = this.isTeacher
          ? [this.pageNum, this.pageSize, '', this.courseId]
          : [this.pageNum, this.pageSize, this.courseId, this.userId] // 添加userId作为第四个参数

        try {
          const response = await fetchFunction(...params)
          if (response && response.code === 1 && response.data) {
            this.notices = response.data.records || []
            this.total = response.data.total || 0
            console.log('课程通知数据加载成功:', this.notices.length, '条')
          } else {
            console.error('获取课程通知列表失败:', response)
            throw new Error((response && response.msg) || '获取课程通知列表失败')
          }
        } catch (error) {
          console.error('获取课程通知列表失败:', error)
          throw error
        }
      },

      // 处理每页显示数量变化
      handleSizeChange(val) {
        this.pageSize = val
        this.refreshModuleData('notices')
      },

      // 处理作业组件错误
      handleHomeworkComponentError(error) {
        console.error('作业组件发生错误:', error)
        this.$message.error('作业管理组件加载失败，请刷新页面重试')
      },

      // 根据作业ID打开作业详情
      openHomeworkById(homeworkId) {
        console.log('打开作业详情，作业ID:', homeworkId)
        const homework = this.homeworkList.find(h => h.id == homeworkId)
        if (homework) {
          // 调用作业组件的查看方法
          this.$refs.homeworkComponent && this.$refs.homeworkComponent.handleView(homework)
          // 清除URL中的homeworkId参数（但是保留tab参数）
          const newQuery = { ...this.$route.query }
          delete newQuery.homeworkId
          this.$router.replace({
            path: this.$route.path,
            query: newQuery
          })
          console.log('清除homeworkId后的URL:', this.$route.path + '?' + new URLSearchParams(newQuery).toString())
        } else {
          console.warn('未找到作业ID:', homeworkId)
        }
      },

      // 重新加载页面
      async retryLoad() {
        console.log('课程详情页面 - 重新加载')
        this.pageLoading = true
        this.pageError = false

        try {
          // 重新初始化页面
          await this.checkUserRole()
          await this.fetchCourseDetail()
          this.hideSidebarAndNavbar()
          this.pageLoading = false
          console.log('课程详情页面 - 重新加载完成')
        } catch (error) {
          console.error('课程详情页面 - 重新加载失败:', error)
          this.pageError = true
          this.pageLoading = false
          this.$message.error('重新加载失败，请刷新页面重试')
        }
      },

      // 处理页码变化
      handleCurrentChange(val) {
        this.pageNum = val
        this.refreshModuleData('notices')
      },

      // 提交通知表单
      submitNoticeForm() {
        this.$refs.noticeForm.validate(valid => {
          if (valid) {
            // 设置课程ID
            this.noticeForm.courseId = this.courseId

            const submitFunction = this.editingNoticeId ? updateCourseNotice : addCourseNotice
            const params = this.editingNoticeId ? [this.editingNoticeId, this.noticeForm] : [this.noticeForm]

            submitFunction(...params).then(response => {
              if (response && response.code === 1) {
                this.$message.success(this.editingNoticeId ? '更新成功' : '添加成功')
                this.dialogVisible = false
                this.refreshModuleData('notices')
              } else {
                this.$message.error(response.msg || (this.editingNoticeId ? '更新失败' : '添加失败'))
              }
            }).catch(error => {
              console.error(this.editingNoticeId ? '更新课程通知失败:' : '添加课程通知失败:', error)
              this.$message.error(this.editingNoticeId ? '更新课程通知失败，请稍后重试' : '添加课程通知失败，请稍后重试')
            })
          } else {
            return false
          }
        })
      },

      // 显示添加课程通知对话框
      showAddNoticeDialog() {
        this.dialogVisible = true
        this.editingNoticeId = null
        this.noticeForm = {
          courseId: this.courseId,
          title: '',
          content: ''
        }
      },

      // 显示编辑课程简介对话框
      showEditProfileDialog() {
        this.profileDialogVisible = true
        this.courseForm = {
          id: this.courseId,
          profile: this.course.profile || '',
          target: this.course.target || ''
        }
      },

      // 显示编辑教学目标对话框
      showEditTargetDialog() {
        this.targetDialogVisible = true
        this.courseForm = {
          id: this.courseId,
          profile: this.course.profile || '',
          target: this.course.target || ''
        }
      },

      // 提交课程简介表单
      submitProfileForm() {
        // 创建更新对象，只包含需要更新的字段
        const updateData = {
          id: this.courseId,
          profile: this.courseForm.profile
        }

        updateCourse(updateData).then(response => {
          if (response && response.code === 1) {
            this.$message.success('课程简介更新成功')
            this.profileDialogVisible = false
            // 更新本地数据
            this.course.profile = this.courseForm.profile
          } else {
            this.$message.error((response && response.msg) || '课程简介更新失败')
          }
        }).catch(error => {
          console.error('更新课程简介失败:', error)
          this.$message.error('更新课程简介失败，请稍后重试')
        })
      },

      // 提交教学目标表单
      submitTargetForm() {
        // 创建更新对象，只包含需要更新的字段
        const updateData = {
          id: this.courseId,
          target: this.courseForm.target
        }

        updateCourse(updateData).then(response => {
          if (response && response.code === 1) {
            this.$message.success('教学目标更新成功')
            this.targetDialogVisible = false
            // 更新本地数据
            this.course.target = this.courseForm.target
          } else {
            this.$message.error((response && response.msg) || '教学目标更新失败')
          }
        }).catch(error => {
          console.error('更新教学目标失败:', error)
          this.$message.error('更新教学目标失败，请稍后重试')
        })
      },

      // ==================== 课程资料相关方法 ====================

      // 获取课程资料列表
      async fetchCourseResources() {
        try {
          const response = await getCourseResourceList(this.courseId)
          if (response.code === 1) {
            this.courseResources = response.data || []
            console.log('课程资料数据加载成功:', this.courseResources.length, '条')
          } else {
            throw new Error(response.msg || '获取课程资料失败')
          }
        } catch (error) {
          console.error('获取课程资料失败:', error)
          throw error
        }
      },

      // 显示上传对话框
      showUploadDialog() {
        this.uploadDialogVisible = true
        this.uploadForm = {
          file: null,
          description: ''
        }
        this.fileList = []
      },

      // 文件选择处理
      handleFileChange(file, fileList) {
        this.uploadForm.file = file.raw
        this.fileList = fileList
      },

      // 提交上传
      async submitUpload() {
        if (!this.uploadForm.file) {
          this.$message.warning('请选择文件')
          return
        }

        this.uploading = true
        try {
          const response = await uploadCourseResource(
            this.courseId,
            this.uploadForm.file,
            this.uploadForm.description
          )
          if (response.code === 1) {
            this.$message.success('上传成功')
            this.uploadDialogVisible = false
            this.refreshModuleData('resources') // 刷新列表
          } else {
            this.$message.error(response.msg || '上传失败')
          }
        } catch (error) {
          console.error('上传失败:', error)
          this.$message.error('上传失败: ' + (error.message || '未知错误'))
        } finally {
          this.uploading = false
        }
      },

      // 编辑资料
      editResource(resource) {
        this.editDialogVisible = true
        this.editForm = {
          id: resource.id,
          originalFileName: resource.originalFileName,
          description: resource.description || '',
          file: null
        }
        this.editFileList = []
      },

      // 编辑文件选择处理
      handleEditFileChange(file, fileList) {
        this.editForm.file = file.raw
        this.editFileList = fileList
      },

      // 提交编辑
      async submitEdit() {
        this.editing = true
        try {
          const response = await updateCourseResource(
            this.editForm.id,
            this.editForm.description,
            this.editForm.file
          )
          if (response.code === 1) {
            this.$message.success('修改成功')
            this.editDialogVisible = false
            this.refreshModuleData('resources') // 刷新列表
          } else {
            this.$message.error(response.msg || '修改失败')
          }
        } catch (error) {
          console.error('修改失败:', error)
          this.$message.error('修改失败: ' + (error.message || '未知错误'))
        } finally {
          this.editing = false
        }
      },

      // 删除资料
      async deleteResource(resource) {
        try {
          await this.$confirm('确定要删除这个资料吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          })

          const response = await deleteCourseResource(resource.id)
          if (response.code === 1) {
            this.$message.success('删除成功')
            this.refreshModuleData('resources') // 刷新列表
          } else {
            this.$message.error(response.msg || '删除失败')
          }
        } catch (error) {
          if (error !== 'cancel') {
            console.error('删除失败:', error)
            this.$message.error('删除失败: ' + (error.message || '未知错误'))
          }
        }
      },

      // 下载资料
      async downloadResource(resource) {
        console.log('开始下载资源:', resource)
        try {
          // 如果资源有fileUrl，直接打开
          if (resource.fileUrl) {
            console.log('使用资源URL直接打开:', resource.fileUrl)
            const url = resource.fileUrl
            // 确保URL以http开头
            if (url.startsWith('http://') || url.startsWith('https://')) {
              window.open(url, '_blank')
              this.$message.success('正在打开资源')
              this.refreshModuleData('resources') // 刷新下载次数
              return
            } else {
              console.warn('资源URL格式不正确:', url)
              this.$message.warning('资源URL格式不正确')
            }
          }

          // 否则通过API下载
          console.log('通过API下载资源，资源ID:', resource.id)
          const response = await downloadCourseResource(resource.id)

          // 根据文件类型创建正确的Blob
          const fileExtension = resource.originalFileName.split('.').pop().toLowerCase()
          let mimeType = 'application/octet-stream'

          // 根据文件扩展名设置MIME类型
          switch (fileExtension) {
            case 'pdf':
              mimeType = 'application/pdf'
              break
            case 'doc':
              mimeType = 'application/msword'
              break
            case 'docx':
              mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              break
            case 'xls':
              mimeType = 'application/vnd.ms-excel'
              break
            case 'xlsx':
              mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              break
            case 'ppt':
              mimeType = 'application/vnd.ms-powerpoint'
              break
            case 'pptx':
              mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
              break
            case 'zip':
              mimeType = 'application/zip'
              break
            case 'rar':
              mimeType = 'application/x-rar-compressed'
              break
            case 'txt':
              mimeType = 'text/plain'
              break
            case 'jpg':
            case 'jpeg':
              mimeType = 'image/jpeg'
              break
            case 'png':
              mimeType = 'image/png'
              break
            case 'gif':
              mimeType = 'image/gif'
              break
            case 'mp4':
              mimeType = 'video/mp4'
              break
            case 'avi':
              mimeType = 'video/x-msvideo'
              break
            case 'mov':
              mimeType = 'video/quicktime'
              break
            case 'wmv':
              mimeType = 'video/x-ms-wmv'
              break
            case 'flv':
              mimeType = 'video/x-flv'
              break
            case 'webm':
              mimeType = 'video/webm'
              break
            case 'mkv':
              mimeType = 'video/x-matroska'
              break
          }

          // 创建Blob对象
          const blob = new Blob([response.data], { type: mimeType })

          // 创建下载链接
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = resource.originalFileName
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          this.$message.success('下载成功')
          this.refreshModuleData('resources') // 刷新下载次数
        } catch (error) {
          console.error('下载失败:', error)
          this.$message.error('下载失败: ' + (error.message || '未知错误'))
        }
      },

      // 获取文件图标
      getFileIcon(fileName) {
        if (!fileName) return 'el-icon-document'
        const extension = fileName.split('.').pop().toLowerCase()

        // 视频文件
        const videoTypes = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv']
        if (videoTypes.includes(extension)) {
          return 'el-icon-video-camera'
        }

        // PDF文件
        if (extension === 'pdf') {
          return 'el-icon-document'
        }

        // Word文件
        if (['doc', 'docx'].includes(extension)) {
          return 'el-icon-document'
        }

        // PowerPoint文件
        if (['ppt', 'pptx'].includes(extension)) {
          return 'el-icon-document'
        }

        // 压缩文件
        if (['zip', 'rar', '7z'].includes(extension)) {
          return 'el-icon-folder-opened'
        }

        return 'el-icon-document'
      },

      // 格式化文件大小
      formatFileSize(bytes) {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
      },

      // 格式化时间
      formatTime(time) {
        if (!time) return ''
        const date = new Date(time)
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
      },

      // ==================== 作业管理相关方法 ====================

      // 获取作业列表
      async fetchHomeworkList() {
        try {
          // 根据课程ID获取作业列表
          const response = await getHomeworkListByCourseId(this.courseId)
          if (response.code === 1) {
            this.homeworkList = response.data || []
            console.log('作业数据加载成功:', this.homeworkList.length, '条')
          } else {
            throw new Error(response.msg || '获取作业列表失败')
          }
        } catch (error) {
          console.error('获取作业列表失败:', error)
          this.homeworkList = []
          throw error
        }
      },

      // 获取作业状态类型
      getHomeworkStatusType(status) {
        const statusMap = {
          'active': 'success',
          'expired': 'danger',
          'draft': 'info'
        }
        return statusMap[status] || 'info'
      },

      // 获取作业状态文本
      getHomeworkStatusText(status) {
        const statusMap = {
          'active': '进行中',
          'expired': '已过期',
          'draft': '草稿'
        }
        return statusMap[status] || '未知'
      },

      // 显示添加作业对话框
      showAddHomeworkDialog() {
        this.$message.info('添加作业功能开发中...')
      },

      // 查看作业
      viewHomework(homework) {
        this.$message.info(`查看作业：${homework.title}`)
      },

      // 编辑作业
      editHomework(homework) {
        this.$message.info(`编辑作业：${homework.title}`)
      },

      // 删除作业
      deleteHomework(homework) {
        this.$confirm(`确定要删除作业"${homework.title}"吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.$message.success('删除成功')
          this.refreshModuleData('homework')
        }).catch(() => {
          this.$message.info('已取消删除')
        })
      },

      // 提交作业
      submitHomework(homework) {
        this.$message.info(`提交作业：${homework.title}`)
      },

      // 子知识点管理方法
      async manageChildrenKnowledge(parentKnowledge) {
        this.currentParentKnowledge = parentKnowledge
        this.childrenKnowledgeDialogVisible = true
        await this.refreshChildrenKnowledge()
      },

      async refreshChildrenKnowledge() {
        if (!this.currentParentKnowledge) return
        try {
          const res = await getChildrenByParentId(this.currentParentKnowledge.id)
          if (res && res.code === 1) {
            this.currentChildrenKnowledge = res.data || []
          } else {
            this.$message.error((res && res.msg) || '获取子知识点失败')
          }
        } catch (error) {
          console.error('获取子知识点失败:', error)
          this.$message.error('获取子知识点失败')
        }
      },

      showAddChildKnowledgeDialog() {
        this.availableKnowledgePoints = this.knowledgePoints.filter(kp => kp.id !== this.currentParentKnowledge.id)
        this.childKnowledgeForm = { childId: null, relationType: 1 }
        this.addChildKnowledgeDialogVisible = true
      },

      async saveChildKnowledge() {
        if (!this.childKnowledgeForm.childId) {
          this.$message.warning('请选择要添加的知识点')
          return
        }
        try {
          const res = await addChildKnowledgePoint(
            this.currentParentKnowledge.id,
            this.childKnowledgeForm.childId,
            this.childKnowledgeForm.relationType
          )
          if (res && res.code === 1) {
            this.$message.success('添加子知识点成功')
            this.addChildKnowledgeDialogVisible = false
            await this.refreshChildrenKnowledge()
          } else {
            this.$message.error((res && res.msg) || '添加失败')
          }
        } catch (error) {
          console.error('添加子知识点失败:', error)
          this.$message.error('添加失败')
        }
      },

      editChildKnowledge(childKnowledge) {
        this.knowledgeForm = { ...childKnowledge }
        this.knowledgeDialogVisible = true
      },

      async removeChildKnowledge(childKnowledge) {
        try {
          await this.$confirm('确定要移除此子知识点吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          })
          const res = await removeChildKnowledgePoint(
            this.currentParentKnowledge.id,
            childKnowledge.id
          )
          if (res && res.code === 1) {
            this.$message.success('移除成功')
            await this.refreshChildrenKnowledge()
          } else {
            this.$message.error((res && res.msg) || '移除失败')
          }
        } catch (error) {
          if (error !== 'cancel') {
            console.error('移除子知识点失败:', error)
            this.$message.error('移除失败')
          }
        }
      },

      // ============ 章节资源管理相关 ============
      async loadChapterResources() {
        // 为每个章节加载关联的资源
        for (const chapter of this.chapters) {
          try {
            const res = await getChapterResourcesWithDetails(chapter.id)
            if (res && res.code === 1) {
              this.$set(this.chapterResources, chapter.id, res.data || [])
            }
          } catch (error) {
            console.error(`加载章节${chapter.id}的资源失败:`, error)
            this.$set(this.chapterResources, chapter.id, [])
          }
        }
      },

      async manageChapterResources(chapter) {
        this.currentChapter = chapter
        this.chapterResourceDialogVisible = true
        await this.fetchChapterResources(chapter.id)
      },

      async fetchChapterResources(chapterId) {
        try {
          const res = await getChapterResourcesWithDetails(chapterId)
          if (res && res.code === 1) {
            this.currentChapterResources = res.data || []
          } else {
            this.$message.error((res && res.msg) || '获取章节资源失败')
          }
        } catch (error) {
          console.error('获取章节资源失败:', error)
          this.$message.error('获取章节资源失败')
        }
      },

      async showAddResourceToChapterDialog() {
        // 确保已加载课程资源数据
        if (!this.dataLoaded.resources) {
          await this.fetchCourseResources()
        }

        // 过滤出可以关联的资源（排除已经属于当前章节的资源）
        this.availableCourseResources = this.courseResources.filter(resource =>
          !this.currentChapterResources.some(cr => cr.id === resource.id)
        )

        this.selectedResources = []
        this.linkResourceDialogVisible = true
      },

      handleResourceSelectionChange(selection) {
        this.selectedResources = selection
      },

      async linkSelectedResourcesToChapter() {
        if (this.selectedResources.length === 0) {
          this.$message.warning('请选择要关联的资源')
          return
        }

        try {
          const resourceIds = this.selectedResources.map(resource => resource.id)
          const res = await batchAssociateResourcesToChapter({
            chapterId: this.currentChapter.id,
            resourceIds: resourceIds
          })

          if (res && res.code === 1) {
            this.$message.success(`成功关联 ${this.selectedResources.length} 个资源到此章节`)
            this.linkResourceDialogVisible = false

            // 刷新相关数据
            await this.refreshChapterResources()
            await this.loadChapterResources() // 刷新章节表格中的资源显示
          } else {
            this.$message.error((res && res.msg) || '关联资源失败')
          }
        } catch (error) {
          console.error('关联资源失败:', error)
          this.$message.error('关联资源失败')
        }
      },

      async refreshChapterResources() {
        if (this.currentChapter) {
          await this.fetchChapterResources(this.currentChapter.id)
        }
      },

      async removeResourceFromChapter(resource) {
        try {
          await this.$confirm('确认从该章节移除该资源？', '提示')
          const res = await disassociateResourceFromChapter(this.currentChapter.id, resource.id)
          if (res && res.code === 1) {
            this.$message.success('移除成功')
            await this.refreshChapterResources()
            await this.loadChapterResources() // 刷新章节表格中的资源显示
          } else {
            this.$message.error((res && res.msg) || '移除失败')
          }
        } catch (error) {
          if (error !== 'cancel') {
            console.error('移除资源失败:', error)
            this.$message.error('移除失败')
          }
        }
      },

      // ============ 考试详情相关 ============
      // 打开考试详情
      openExamDetail(exam) {
        console.log('打开考试详情:', exam)
        this.currentExam = exam
        this.examDetailVisible = true
      },

      // 关闭考试详情
      closeExamDetail() {
        this.examDetailVisible = false
        this.currentExam = null
      }
    }
  }

  </script>

  <style scoped>
  .course-detail-container {
    padding: 20px;
    background-color: #f9fafb;
    min-height: calc(100vh - 84px);
  }

  /* 全屏页面样式 */
  .fullscreen-page {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background-color: #f9fafb;
    overflow: auto;
    min-height: 100vh;
    width: 100vw;
    padding-top: 0;
  }

  .course-header {
    display: flex;
    align-items: flex-start;
    margin-bottom: 30px;
  }

  .back-button {
    margin-right: 20px;
  }

  .course-title-section {
    flex: 1;
  }

  .course-title-with-cover {
    display: flex;
    align-items: flex-start;
    gap: 20px;
  }

  .course-cover-section {
    flex-shrink: 0;
  }

  .course-cover-large {
    width: 120px;
    height: 90px;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid #e4e7ed;
  }

  .course-info-section {
    flex: 1;
  }

  .course-title-section h1 {
    margin: 0 0 10px 0;
    font-size: 24px;
    color: #303133;
  }

  .course-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    color: #606266;
  }

  .meta-item {
    display: flex;
    align-items: center;
  }

  .meta-item i {
    margin-right: 5px;
  }

  .course-content {
    display: flex;
    gap: 30px;
  }

  .course-sidebar {
    width: 200px;
    height: 700px;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .course-menu {
    border-right: none;
  }

  .course-main {
    flex: 1;
    background-color: #fff;
    border-radius: 4px;
    padding: 20px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .course-section {
    margin-bottom: 30px;
  }

  .course-section h3 {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 18px;
    color: #303133;
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 10px;
  }

  .course-text {
    line-height: 1.6;
    color: #606266;
    white-space: pre-line;
  }

  .empty-tip {
    text-align: center;
    padding: 30px;
    color: #909399;
  }

  .empty-tip i {
    font-size: 48px;
    margin-bottom: 10px;
  }

  .resource-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .resource-item {
    display: flex;
    align-items: center;
    padding: 15px 0;
    border-bottom: 1px solid #ebeef5;
  }

  .resource-item:last-child {
    border-bottom: none;
  }

  .resource-item i {
    margin-right: 15px;
    color: #409EFF;
    font-size: 20px;
  }

  .resource-info {
    flex: 1;
    margin-right: 15px;
  }

  .resource-name {
    font-size: 16px;
    font-weight: 500;
    color: #303133;
    margin-bottom: 5px;
  }

  .resource-meta {
    display: flex;
    gap: 15px;
    font-size: 12px;
    color: #909399;
    margin-bottom: 5px;
  }

  .resource-description {
    font-size: 14px;
    color: #606266;
    line-height: 1.4;
  }

  .resource-actions {
    display: flex;
    gap: 8px;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .section-header h3 {
    margin: 0;
    font-size: 18px;
    color: #303133;
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 10px;
    flex: 1;
  }

  .el-upload__tip {
    color: #999;
    font-size: 12px;
    margin-top: 5px;
  }

  .notice-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .notice-header h3 {
    margin: 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
  }

  .notice-actions {
    display: flex;
    gap: 10px;
  }

  .notice-list {
    margin-bottom: 20px;
  }

  .notice-item {
    padding: 15px 0;
    border-bottom: 1px solid #ebeef5;
  }

  .notice-item:last-child {
    border-bottom: none;
  }

  .notice-item-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
  }

  .notice-title {
    margin: 0;
    font-size: 16px;
    font-weight: bold;
    color: #303133;
  }

  .notice-meta {
    display: flex;
    align-items: center;
    color: #909399;
    font-size: 12px;
  }

  .notice-time {
    margin-right: 15px;
  }

  .notice-content {
    color: #606266;
    line-height: 1.6;
    white-space: pre-line;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .section-header h3 {
    margin: 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
  }

  /* 作业管理样式 */
  .homework-actions {
    display: flex;
    gap: 10px;
  }

  .course-teacher-info {
    margin-bottom: 20px;
  }

  .info-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .info-content {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;
    padding: 10px 0;
  }

  .info-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
  }

  .info-item i {
    margin-right: 8px;
    color: #409EFF;
    font-size: 16px;
  }

  .info-item .label {
    font-weight: 500;
    color: #606266;
    margin-right: 8px;
    min-width: 80px;
  }

  .info-item .value {
    color: #303133;
    font-weight: 400;
  }

  .homework-list {
    margin-top: 20px;
  }

  .empty-tip {
    text-align: center;
    padding: 40px;
    color: #909399;
  }

  .empty-tip i {
    font-size: 48px;
    margin-bottom: 15px;
    color: #c0c4cc;
  }

  .empty-tip p {
    margin: 10px 0 20px 0;
    font-size: 16px;
  }

  /* 加载状态样式 */
  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    border-radius: 8px;
    background-color: #fafafa;
  }

  /* 页面加载状态样式 */
  .page-loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* 页面错误状态样式 */
  .page-error {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
  }

  .error-content {
    text-align: center;
    padding: 40px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  }

  .error-content i {
    font-size: 64px;
    color: #f56c6c;
    margin-bottom: 20px;
  }

  .error-content h3 {
    color: #303133;
    margin-bottom: 10px;
  }

  .error-content p {
    color: #606266;
    margin-bottom: 20px;
  }

  /* 考试详情对话框样式 */
  .exam-detail-dialog {
    margin-top: 5vh !important;
  }

  .exam-detail-dialog .el-dialog__body {
    padding: 0;
    max-height: 80vh;
    overflow-y: auto;
  }

  .error-content .el-button {
    margin: 0 10px;
  }
</style>

<style>
/* 确保弹窗不被遮挡 */
.el-dialog__wrapper {
  z-index: 10000 !important;
}

.el-overlay {
  z-index: 9999 !important;
}

.el-dialog {
  z-index: 10001 !important;
}
</style>
